// pages/Files/Files.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Upload,
  Search,
  Filter,
  Download,
  Trash2,
  Eye,
  MoreVertical,
  FileText,
  Image as ImageIcon,
  Video,
  Music,
  File as FileIcon,
  Folder,
  Grid,
  List,
  SortAsc,
  SortDesc,
  Calendar,
  User,
  ChevronDown,
  X as CloseIcon,
  Loader,
  AlertCircle,
  ExternalLink,
  Link as LinkIcon,
} from "lucide-react";
import apiService from "../../services/api";
import { useTheme } from "../../contexts/ThemeContext";

const Files = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [showUploadModal, setShowUploadModal] = useState(false);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFiles, setTotalFiles] = useState(0);
  const limit = 24;

  const [isDragging, setIsDragging] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);

  useEffect(() => {
    fetchFiles();
  }, [page, sortBy, sortOrder, searchQuery, filterType]);

  const fetchFiles = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAllFiles({
        page,
        limit,
        sortBy,
        sortOrder,
        search: searchQuery,
        fileType: filterType !== "all" ? filterType : undefined,
      });

      const filesArray = response.data?.data || response.data || [];
      setFiles(filesArray);
      setTotalPages(response.data?.pagination?.totalPages || 1);
      setTotalFiles(response.data?.pagination?.total || filesArray.length);
    } catch (error) {
      console.error("Error fetching files:", error);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;

    setUploading(true);

    try {
      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i];
        const formData = new FormData();
        formData.append("file", file);
        await apiService.uploadFile(formData);
      }

      fetchFiles();
      setShowUploadModal(false);
    } catch (error) {
      console.error("Error uploading files:", error);
      alert("Failed to upload some files");
    } finally {
      setUploading(false);
    }
  };

  const handleFileInput = (event) => {
    const fileList = event.target.files;
    handleUploadFiles(fileList);
    event.target.value = "";
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm("Are you sure you want to delete this file?")) return;

    try {
      await apiService.deleteFile(fileId);
      fetchFiles();
      selectedFiles.delete(fileId);
      setSelectedFiles(new Set(selectedFiles));
    } catch (error) {
      console.error("Error deleting file:", error);
      alert("Failed to delete file");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedFiles.size === 0) return;
    if (
      !window.confirm(
        `Are you sure you want to delete ${selectedFiles.size} file(s)?`,
      )
    )
      return;

    try {
      await Promise.all(
        Array.from(selectedFiles).map((fileId) =>
          apiService.deleteFile(fileId),
        ),
      );
      fetchFiles();
      setSelectedFiles(new Set());
    } catch (error) {
      console.error("Error deleting files:", error);
      alert("Failed to delete some files");
    }
  };

  const toggleFileSelection = (fileId) => {
    const newSelection = new Set(selectedFiles);
    if (newSelection.has(fileId)) {
      newSelection.delete(fileId);
    } else {
      newSelection.add(fileId);
    }
    setSelectedFiles(newSelection);
  };

  const selectAllFiles = () => {
    if (selectedFiles.size === files.length) {
      setSelectedFiles(new Set());
    } else {
      setSelectedFiles(new Set(files.map((f) => f.id)));
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter((prev) => prev + 1);
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter((prev) => prev - 1);
    if (dragCounter - 1 === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setDragCounter(0);

    const fileList = e.dataTransfer.files;
    if (fileList && fileList.length > 0) {
      handleUploadFiles(fileList);
    }
  };

  const getFullFileUrl = (fileUrl) => {
    if (!fileUrl) return null;
    if (fileUrl.startsWith("http://") || fileUrl.startsWith("https://")) {
      return fileUrl;
    }
    const S3_BASE_URL = "https://fibre-crm.s3.eu-west-2.amazonaws.com/";
    return S3_BASE_URL + fileUrl;
  };

  const getFileIcon = (fileType, size = 24) => {
    if (fileType?.startsWith("image/"))
      return <ImageIcon size={size} className="text-blue-400" />;
    if (fileType?.startsWith("video/"))
      return <Video size={size} className="text-purple-400" />;
    if (fileType?.startsWith("audio/"))
      return <Music size={size} className="text-green-400" />;
    if (fileType?.includes("pdf"))
      return <FileText size={size} className="text-red-400" />;
    if (fileType?.includes("word") || fileType?.includes("document"))
      return <FileText size={size} className="text-blue-400" />;
    if (fileType?.includes("sheet") || fileType?.includes("excel"))
      return <FileText size={size} className="text-green-400" />;
    if (fileType?.includes("presentation") || fileType?.includes("powerpoint"))
      return <FileText size={size} className="text-orange-400" />;
    return (
      <FileIcon
        size={size}
        className={isDarkMode ? "text-gray-400" : "text-gray-500"}
      />
    );
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const getFileTypeLabel = (fileType) => {
    if (!fileType) return "Unknown";
    if (fileType.startsWith("image/")) return "Image";
    if (fileType.startsWith("video/")) return "Video";
    if (fileType.startsWith("audio/")) return "Audio";
    if (fileType.includes("pdf")) return "PDF";
    if (fileType.includes("word") || fileType.includes("document"))
      return "Document";
    if (fileType.includes("sheet") || fileType.includes("excel"))
      return "Spreadsheet";
    if (fileType.includes("presentation") || fileType.includes("powerpoint"))
      return "Presentation";
    if (fileType.includes("zip") || fileType.includes("rar")) return "Archive";
    return fileType.split("/")[1]?.toUpperCase() || "File";
  };

  return (
    <div
      className={`min-h-screen font-inter transition-colors duration-300 ${isDarkMode ? "bg-[#0C0E12] text-white" : "bg-[#F4F5F7] text-gray-900"}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Header */}
      <div
        className={`border-b ${isDarkMode ? "border-gray-700 bg-[#13161B]" : "border-gray-200 bg-white"}`}
      >
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1
                className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Files
              </h1>
              <p
                className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                {totalFiles} file{totalFiles !== 1 ? "s" : ""} total
              </p>
            </div>

            <div className="flex items-center gap-3">
              <label className="cursor-pointer">
                <input
                  type="file"
                  multiple
                  onChange={handleFileInput}
                  className="hidden"
                  disabled={uploading}
                />
                <div className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50">
                  {uploading ? (
                    <>
                      <Loader size={16} className="animate-spin" />
                      <span>Uploading...</span>
                    </>
                  ) : (
                    <>
                      <Upload size={16} />
                      <span>Upload Files</span>
                    </>
                  )}
                </div>
              </label>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="flex items-center gap-3">
            <div className="flex-1 relative">
              <Search
                className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                size={20}
              />
              <input
                type="text"
                placeholder="Search files..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                className={`w-full pl-10 pr-4 py-2 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white placeholder-gray-400" : "bg-gray-100 border-gray-200 text-gray-900 placeholder-gray-500"} border rounded-lg focus:outline-none focus:border-blue-500`}
              />
            </div>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                showFilters
                  ? "bg-blue-600 text-white"
                  : isDarkMode
                    ? "bg-gray-800 text-gray-300 hover:bg-gray-700"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              <Filter size={16} />
              <span>Filters</span>
              {filterType !== "all" && (
                <span className="w-2 h-2 rounded-full bg-blue-400"></span>
              )}
            </button>

            <div
              className={`flex items-center gap-1 ${isDarkMode ? "bg-gray-800" : "bg-gray-200"} rounded-lg p-1`}
            >
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 rounded transition-colors ${
                  viewMode === "grid"
                    ? isDarkMode
                      ? "bg-gray-700 text-white"
                      : "bg-gray-300 text-gray-900"
                    : isDarkMode
                      ? "text-gray-400 hover:text-white"
                      : "text-gray-500 hover:text-gray-900"
                }`}
              >
                <Grid size={16} />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-2 rounded transition-colors ${
                  viewMode === "list"
                    ? isDarkMode
                      ? "bg-gray-700 text-white"
                      : "bg-gray-300 text-gray-900"
                    : isDarkMode
                      ? "text-gray-400 hover:text-white"
                      : "text-gray-500 hover:text-gray-900"
                }`}
              >
                <List size={16} />
              </button>
            </div>

            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [newSortBy, newSortOrder] = e.target.value.split("-");
                setSortBy(newSortBy);
                setSortOrder(newSortOrder);
              }}
              className={`px-4 py-2 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-gray-100 border-gray-200 text-gray-900"} border rounded-lg focus:outline-none focus:border-blue-500`}
            >
              <option value="createdAt-DESC">Newest First</option>
              <option value="createdAt-ASC">Oldest First</option>
              <option value="title-ASC">Name A-Z</option>
              <option value="title-DESC">Name Z-A</option>
              <option value="contentSize-DESC">Largest First</option>
              <option value="contentSize-ASC">Smallest First</option>
            </select>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div
              className={`mt-4 p-4 ${isDarkMode ? "bg-gray-800/50" : "bg-gray-100"} rounded-lg`}
            >
              <div className="flex items-center justify-between mb-3">
                <h3
                  className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Filter by Type
                </h3>
                <button
                  onClick={() => setFilterType("all")}
                  className="text-sm text-blue-400 hover:text-blue-300"
                >
                  Clear All
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: "all", label: "All Files", icon: FileIcon },
                  { value: "image", label: "Images", icon: ImageIcon },
                  { value: "video", label: "Videos", icon: Video },
                  { value: "audio", label: "Audio", icon: Music },
                  { value: "pdf", label: "PDFs", icon: FileText },
                  { value: "document", label: "Documents", icon: FileText },
                ].map((type) => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.value}
                      onClick={() => {
                        setFilterType(type.value);
                        setPage(1);
                      }}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                        filterType === type.value
                          ? "bg-blue-600 text-white"
                          : isDarkMode
                            ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`}
                    >
                      <Icon size={14} />
                      {type.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Bulk Actions */}
          {selectedFiles.size > 0 && (
            <div
              className={`mt-4 flex items-center justify-between p-3 bg-blue-600/10 border border-blue-600/20 rounded-lg`}
            >
              <div className="flex items-center gap-3">
                <span className="text-sm text-blue-400">
                  {selectedFiles.size} file{selectedFiles.size !== 1 ? "s" : ""}{" "}
                  selected
                </span>
                <button
                  onClick={() => setSelectedFiles(new Set())}
                  className={`text-sm ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}
                >
                  Clear Selection
                </button>
              </div>
              <button
                onClick={handleBulkDelete}
                className="flex items-center gap-2 px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded-lg text-sm text-white"
              >
                <Trash2 size={14} />
                Delete Selected
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1600px] mx-auto px-6 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader
              className={`animate-spin ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}
              size={32}
            />
          </div>
        ) : files.length === 0 ? (
          <div className="text-center py-20">
            <FileIcon
              size={64}
              className={`mx-auto mb-4 ${isDarkMode ? "text-gray-600" : "text-gray-400"}`}
            />
            <h3
              className={`text-xl font-semibold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              No files found
            </h3>
            <p
              className={`mb-6 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              {searchQuery || filterType !== "all"
                ? "Try adjusting your filters"
                : "Upload your first file to get started"}
            </p>
            {!searchQuery && filterType === "all" && (
              <label className="cursor-pointer">
                <input
                  type="file"
                  multiple
                  onChange={handleFileInput}
                  className="hidden"
                />
                <div className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors text-white">
                  <Upload size={20} />
                  Upload Files
                </div>
              </label>
            )}
          </div>
        ) : (
          <>
            {viewMode === "grid" && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                {files.map((file) => (
                  <FileGridCard
                    key={file.id}
                    file={file}
                    isSelected={selectedFiles.has(file.id)}
                    onSelect={() => toggleFileSelection(file.id)}
                    onDelete={() => handleDeleteFile(file.id)}
                    getFileIcon={getFileIcon}
                    formatFileSize={formatFileSize}
                    getFileTypeLabel={getFileTypeLabel}
                    getFullFileUrl={getFullFileUrl}
                    isDarkMode={isDarkMode}
                  />
                ))}
              </div>
            )}

            {viewMode === "list" && (
              <div
                className={`${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"} rounded-lg border overflow-hidden`}
              >
                <table className="w-full">
                  <thead>
                    <tr
                      className={`border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                    >
                      <th className="w-10 p-3">
                        <input
                          type="checkbox"
                          checked={
                            selectedFiles.size === files.length &&
                            files.length > 0
                          }
                          onChange={selectAllFiles}
                          className="rounded"
                        />
                      </th>
                      <th
                        className={`text-left p-3 text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Name
                      </th>
                      <th
                        className={`text-left p-3 text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Type
                      </th>
                      <th
                        className={`text-left p-3 text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Size
                      </th>
                      <th
                        className={`text-left p-3 text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Uploaded
                      </th>
                      <th
                        className={`text-left p-3 text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Uploaded By
                      </th>
                      <th className="w-20"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {files.map((file) => (
                      <FileListRow
                        key={file.id}
                        file={file}
                        isSelected={selectedFiles.has(file.id)}
                        onSelect={() => toggleFileSelection(file.id)}
                        onDelete={() => handleDeleteFile(file.id)}
                        getFileIcon={getFileIcon}
                        formatFileSize={formatFileSize}
                        getFileTypeLabel={getFileTypeLabel}
                        getFullFileUrl={getFullFileUrl}
                        isDarkMode={isDarkMode}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`px-4 py-2 ${isDarkMode ? "bg-gray-800 hover:bg-gray-700" : "bg-gray-200 hover:bg-gray-300"} rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Previous
                </button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(
                      (p) =>
                        p === 1 ||
                        p === totalPages ||
                        (p >= page - 2 && p <= page + 2),
                    )
                    .map((p, idx, arr) => (
                      <React.Fragment key={p}>
                        {idx > 0 && arr[idx - 1] !== p - 1 && (
                          <span
                            className={`px-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            ...
                          </span>
                        )}
                        <button
                          onClick={() => setPage(p)}
                          className={`w-10 h-10 rounded-lg ${
                            page === p
                              ? "bg-blue-600 text-white"
                              : isDarkMode
                                ? "bg-gray-800 hover:bg-gray-700 text-white"
                                : "bg-gray-200 hover:bg-gray-300 text-gray-900"
                          }`}
                        >
                          {p}
                        </button>
                      </React.Fragment>
                    ))}
                </div>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className={`px-4 py-2 ${isDarkMode ? "bg-gray-800 hover:bg-gray-700" : "bg-gray-200 hover:bg-gray-300"} rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Drag and Drop Overlay */}
      {isDragging && (
        <div className="fixed inset-0 bg-blue-500/20 backdrop-blur-sm flex items-center justify-center z-50 pointer-events-none">
          <div
            className={`${isDarkMode ? "bg-[#13161B]" : "bg-white"} rounded-lg border-2 border-dashed border-blue-500 p-12 text-center`}
          >
            <Upload size={64} className="mx-auto mb-4 text-blue-400" />
            <p
              className={`text-2xl font-semibold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              Drop files to upload
            </p>
            <p className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
              Release to add files to your library
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// File Grid Card Component
const FileGridCard = ({
  file,
  isSelected,
  onSelect,
  onDelete,
  getFileIcon,
  formatFileSize,
  getFileTypeLabel,
  getFullFileUrl,
  isDarkMode,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const fullFileUrl = getFullFileUrl(file.fileUrl);

  return (
    <div
      className={`relative group rounded-lg border transition-all ${
        isSelected
          ? "border-blue-500 ring-2 ring-blue-500/20"
          : isDarkMode
            ? "bg-[#13161B] border-gray-700 hover:border-gray-600"
            : "bg-white border-gray-200 hover:border-gray-300"
      }`}
    >
      <div className="absolute top-2 left-2 z-10">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onSelect}
          className="w-5 h-5 rounded"
          onClick={(e) => e.stopPropagation()}
        />
      </div>

      <div className="absolute top-2 right-2 z-10">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowMenu(!showMenu);
          }}
          className={`p-1.5 ${isDarkMode ? "bg-gray-800/80 hover:bg-gray-700" : "bg-gray-200/80 hover:bg-gray-300"} rounded transition-colors opacity-0 group-hover:opacity-100`}
        >
          <MoreVertical size={16} />
        </button>

        {showMenu && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setShowMenu(false)}
            />
            <div
              className={`absolute right-0 top-8 w-48 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"} border rounded-lg shadow-xl z-20`}
            >
              {fullFileUrl && (
                <a
                  href={fullFileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center gap-2 px-4 py-2 text-sm ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-100"} ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  onClick={() => setShowMenu(false)}
                >
                  <ExternalLink size={14} />
                  Open
                </a>
              )}
              {fullFileUrl && (
                <a
                  href={fullFileUrl}
                  download
                  className={`flex items-center gap-2 px-4 py-2 text-sm ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-100"} ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  onClick={() => setShowMenu(false)}
                >
                  <Download size={14} />
                  Download
                </a>
              )}
              <div
                className={`border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"} my-1`}
              ></div>
              <button
                onClick={() => {
                  onDelete();
                  setShowMenu(false);
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-400 hover:bg-gray-800"
              >
                <Trash2 size={14} />
                Delete
              </button>
            </div>
          </>
        )}
      </div>

      <div className="aspect-square flex items-center justify-center p-6">
        {file.fileType?.startsWith("image/") && fullFileUrl ? (
          <img
            src={fullFileUrl}
            alt={file.title}
            className="w-full h-full object-cover rounded"
          />
        ) : (
          getFileIcon(file.fileType, 48)
        )}
      </div>

      <div
        className={`p-3 border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
      >
        <div
          className={`font-medium text-sm truncate mb-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
          title={file.title}
        >
          {file.title}
        </div>
        <div
          className={`flex items-center justify-between text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
        >
          <span>{getFileTypeLabel(file.fileType)}</span>
          <span>{formatFileSize(file.contentSize)}</span>
        </div>
        {file.creator && (
          <div
            className={`text-xs mt-1 truncate ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
          >
            {file.creator.name}
          </div>
        )}
      </div>
    </div>
  );
};

// File List Row Component
const FileListRow = ({
  file,
  isSelected,
  onSelect,
  onDelete,
  getFileIcon,
  formatFileSize,
  getFileTypeLabel,
  getFullFileUrl,
  isDarkMode,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const fullFileUrl = getFullFileUrl(file.fileUrl);

  return (
    <tr
      className={`border-b ${isDarkMode ? "border-gray-700/50 hover:bg-gray-800/30" : "border-gray-200 hover:bg-gray-100/50"} transition-colors`}
    >
      <td className="p-3">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onSelect}
          className="rounded"
        />
      </td>
      <td className="p-3">
        <div className="flex items-center gap-3">
          {getFileIcon(file.fileType, 20)}
          <div className="flex-1 min-w-0">
            <div
              className={`font-medium text-sm truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}
              title={file.title}
            >
              {file.title}
            </div>
          </div>
        </div>
      </td>
      <td
        className={`p-3 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
      >
        {getFileTypeLabel(file.fileType)}
      </td>
      <td
        className={`p-3 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
      >
        {formatFileSize(file.contentSize)}
      </td>
      <td
        className={`p-3 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
      >
        {new Date(file.createdAt).toLocaleDateString()}
      </td>
      <td
        className={`p-3 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
      >
        {file.creator?.name || "—"}
      </td>
      <td className="p-3">
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className={`p-1.5 ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-200"} rounded transition-colors`}
          >
            <MoreVertical size={16} />
          </button>

          {showMenu && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowMenu(false)}
              />
              <div
                className={`absolute right-0 top-8 w-48 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"} border rounded-lg shadow-xl z-20`}
              >
                {fullFileUrl && (
                  <a
                    href={fullFileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-2 px-4 py-2 text-sm ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-100"} ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    onClick={() => setShowMenu(false)}
                  >
                    <ExternalLink size={14} />
                    Open
                  </a>
                )}
                {fullFileUrl && (
                  <a
                    href={fullFileUrl}
                    download
                    className={`flex items-center gap-2 px-4 py-2 text-sm ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-100"} ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    onClick={() => setShowMenu(false)}
                  >
                    <Download size={14} />
                    Download
                  </a>
                )}
                <div
                  className={`border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"} my-1`}
                ></div>
                <button
                  onClick={() => {
                    onDelete();
                    setShowMenu(false);
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-400 hover:bg-gray-800"
                >
                  <Trash2 size={14} />
                  Delete
                </button>
              </div>
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

export default Files;
